"use client";

import dynamic from "next/dynamic";
import type { ComponentProps } from "react";
import { useTheme } from "@/lib/utils/formatting/theme";

// The markdown editor (CodeMirror + markdown preview) is a large dependency and
// is only needed once a user actually edits an MSP doc. Loading it via
// next/dynamic keeps it out of the contacts/companies detail-page bundles that
// import this component but rarely open the editor.
// `dynamic` can't infer props from the forwardRef default export, so type it
// explicitly. Derive the props from the default export rather than importing
// the named MDEditorProps type — the package's wildcard re-exports make that
// name resolve as a namespace (TS2709) at the package root.
type MDEditorProps = ComponentProps<(typeof import("@uiw/react-md-editor"))["default"]>;
const MDEditor = dynamic<MDEditorProps>(() => import("@uiw/react-md-editor"), {
  ssr: false,
  loading: () => (
    <div className="animate-pulse bg-muted" style={{ height: 440 }} aria-hidden="true" />
  ),
});

interface MspContentEditorProps {
  value: string;
  onChange: (value: string) => void;
  height?: number;
}

/**
 * Markdown editor wrapper that respects the app theme.
 *
 * The previous inline usage hardcoded `data-color-mode="light"`, so the editor
 * rendered a white surface in dark mode (unreadable). This derives the color
 * mode from the app theme instead.
 */
export function MspContentEditor({ value, onChange, height }: MspContentEditorProps) {
  const { theme } = useTheme();
  const resolvedHeight =
    height ?? (typeof window !== "undefined" && window.innerWidth < 640 ? 320 : 440);

  return (
    <div className="overflow-hidden rounded-md border border-border" data-color-mode={theme}>
      <MDEditor
        value={value}
        onChange={(v: string | undefined) => onChange(v || "")}
        height={resolvedHeight}
        visibleDragbar={false}
        hideToolbar={false}
        preview="edit"
        data-testid="markdown-editor"
      />
    </div>
  );
}
